import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { JOB_STATUS } from "common/constants";
import { useGetBulkOperationJobsQuery } from "../../../generated/graphql";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { BANNER_TYPES } from "../__styles__/Banner";
import ActionBanner from "../ActionBanner";
import { BannerContainer } from "./__styles__/BulkOperationProgressBanner";
import { getBannerMessage } from "./MessageBuilder";

const DAYS_AGO = 3;
export const POLL_INTERVAL_IN_MILLISECONDS = 5 * 1000; // Poll every 5 seconds
const STORAGE_KEY = "dismissedBulkOperationJobs";

type BulkOperationProgressBannerProps = {
  objectType: string;
  onComplete?: () => void;
  onVisibleBannersChange?: () => void;
};

const getBannerLoading = (status: JOB_STATUS) => {
  return status === JOB_STATUS.PROCESSING || status === JOB_STATUS.UNKNOWN;
};

const getBannerType = (status: JOB_STATUS) => {
  if (status === JOB_STATUS.FAILURE) {
    return BANNER_TYPES.CRITICAL;
  }
  return BANNER_TYPES.INFO;
};

export const BulkOperationProgressBanner = ({
  objectType,
  onComplete,
  onVisibleBannersChange,
}: BulkOperationProgressBannerProps) => {
  const { data } = useGetBulkOperationJobsQuery({
    variables: { objectType, daysAgo: DAYS_AGO },
    pollInterval: POLL_INTERVAL_IN_MILLISECONDS,
    fetchPolicy: "network-only",
  });

  const { getValue, setValue } = useLocalStorage<string[]>({
    key: STORAGE_KEY,
    defaultValue: [],
  });

  const [dismissedJobIds, setDismissedJobIds] = useState<Set<string>>(() => {
    const stored = getValue();
    return new Set(stored || []);
  });

  const previousScriptJobStatusesRef = useRef<Map<string, string>>(new Map());

  // Trigger onComplete when jobs transition to completed/failed status
  useEffect(() => {
    const scriptJobs = data?.bulkOperationJobs ?? [];
    let hasCompletedJob = false;

    scriptJobs.forEach(job => {
      const currentStatus = job.status;
      const previousStatus = previousScriptJobStatusesRef.current.get(job.id);

      if (
        previousStatus &&
        previousStatus !== currentStatus &&
        (currentStatus === JOB_STATUS.SUCCESS ||
          currentStatus === JOB_STATUS.FAILURE)
      ) {
        hasCompletedJob = true;
      }

      previousScriptJobStatusesRef.current.set(job.id, currentStatus);
    });

    if (hasCompletedJob) {
      onComplete?.();
    }
  }, [data, onComplete]);

  const handleDismiss = (jobId: string) => {
    setDismissedJobIds(prev => {
      const updated = new Set(prev);
      updated.add(jobId);
      setValue([...updated]);

      return updated;
    });
  };

  const scriptJobs = data?.bulkOperationJobs ?? [];
  const visibleJobs = scriptJobs.filter(job => !dismissedJobIds.has(job.id));

  const scriptJobsRef = useRef(scriptJobs);

  useLayoutEffect(() => {
    scriptJobsRef.current = scriptJobs;
  }, [scriptJobs]);

  // Before page refresh or navigation, set any completed/failed jobs as dismissed
  useEffect(() => {
    const markFinishedJobsAsDismissed = () => {
      const finishedJobIds = scriptJobsRef.current
        .filter(
          job =>
            job.status === JOB_STATUS.SUCCESS ||
            job.status === JOB_STATUS.FAILURE,
        )
        .map(job => job.id);

      if (finishedJobIds.length > 0) {
        const storedJobs = getValue() || [];
        const updatedJobs = [...new Set([...storedJobs, ...finishedJobIds])];
        setValue(updatedJobs);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        markFinishedJobsAsDismissed();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      markFinishedJobsAsDismissed();
    };
  }, []);

  useEffect(() => {
    onVisibleBannersChange?.();
  }, [visibleJobs.length, onVisibleBannersChange]);

  if (visibleJobs.length === 0) {
    return null;
  }

  return (
    <BannerContainer>
      {visibleJobs.map(job => {
        return (
          <ActionBanner
            key={job.id}
            type={getBannerType(job.status)}
            loading={getBannerLoading(job.status)}
            downloadButtonUrl={job.exportObjectKey || undefined}
            downloadButtonText="Download csv"
            dismissButton={true}
            onDismiss={() => handleDismiss(job.id)}
          >
            {getBannerMessage(job)}
          </ActionBanner>
        );
      })}
    </BannerContainer>
  );
};
